import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getMe, logoutUser } from "@/lib/api";
import {
  LogOut, RefreshCw, Search, ChevronDown, Phone, Mail, Clock,
  Building2, User, FileText, Trash2, Loader2, CheckCircle2,
  Circle, Send, Eye, CreditCard, Globe, XCircle, Download
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: "New", color: "#3B82F6", bg: "#3B82F610", icon: Circle },
  contacted: { label: "Contacted", color: "#F59E0B", bg: "#F59E0B10", icon: Send },
  preview_sent: { label: "Preview Sent", color: "#8B5CF6", bg: "#8B5CF610", icon: Eye },
  paid: { label: "Paid", color: "#10B981", bg: "#10B98110", icon: CreditCard },
  live: { label: "Live", color: "#06B6D4", bg: "#06B6D410", icon: Globe },
  lost: { label: "Lost", color: "#6B7280", bg: "#6B728010", icon: XCircle },
};

const STATUSES = ["new", "contacted", "preview_sent", "paid", "live", "lost"];

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: cfg.color, background: cfg.bg }}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-[#111827] border border-white/[0.06] rounded-lg p-4">
      <p className="text-slate-500 text-xs font-medium mb-1">{label}</p>
      <p className="text-2xl font-extrabold" style={{ color }}>{value}</p>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState("");

  useEffect(() => {
    getMe().then(res => { setUser(res.data); fetchLeads(); }).catch(() => navigate('/login'));
  }, [navigate]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, { credentials: 'include' });
      const data = await res.json();
      setLeads(data.leads || []);
    } catch { /* empty */ }
    setLoading(false);
  };

  const updateStatus = async (leadId, newStatus) => {
    await fetch(`${API_URL}/api/leads/${leadId}/status`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });
    setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, status: newStatus } : l));
  };

  const saveNotes = async (leadId) => {
    await fetch(`${API_URL}/api/leads/${leadId}/notes`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ admin_notes: notesText }),
    });
    setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, admin_notes: notesText } : l));
    setEditingNotes(null);
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    await fetch(`${API_URL}/api/leads/${leadId}`, { method: "DELETE", credentials: "include" });
    setLeads(prev => prev.filter(l => l.lead_id !== leadId));
  };

  const handleLogout = async () => { await logoutUser(); navigate('/'); };

  const filtered = leads.filter(l => {
    if (filter !== "all" && l.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return l.business_name?.toLowerCase().includes(q) || l.owner_name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.business_type?.toLowerCase().includes(q);
    }
    return true;
  });

  const counts = { total: leads.length };
  STATUSES.forEach(s => { counts[s] = leads.filter(l => l.status === s).length; });

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  if (loading && !user) {
    return <div className="min-h-screen bg-[#0A0F1F] flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" size={28} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0F1F]" data-testid="admin-page">
      {/* Header */}
      <div className="border-b border-white/[0.04] bg-[#0A0F1F]/95 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-lg font-extrabold text-white">Site<span className="text-slate-500 font-medium">Smith</span></a>
            <span className="text-xs text-orange-500 font-semibold px-2 py-0.5 bg-orange-500/10 rounded-full">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-500 text-sm hidden sm:block">{user?.email}</span>
            <button onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/api/leads/export/csv`, { credentials: 'include' });
                if (!res.ok) throw new Error('Export failed');
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'sitesmith-leads.csv';
                a.click();
                window.URL.revokeObjectURL(url);
              } catch { alert('Export failed. Please try again.'); }
            }} className="text-slate-500 hover:text-white transition-colors flex items-center gap-1.5 text-xs border border-white/10 px-3 py-1.5 rounded-full hover:bg-white/5" data-testid="export-csv-btn">
              <Download size={13} /> Export CSV
            </button>
            <button onClick={fetchLeads} className="text-slate-500 hover:text-white transition-colors" data-testid="refresh-btn"><RefreshCw size={16} /></button>
            <button onClick={handleLogout} className="text-slate-500 hover:text-white transition-colors" data-testid="admin-logout"><LogOut size={16} /></button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-8" data-testid="stats-row">
          <StatCard label="Total Leads" value={counts.total} color="#F8FAFC" />
          <StatCard label="New" value={counts.new} color="#3B82F6" />
          <StatCard label="Contacted" value={counts.contacted} color="#F59E0B" />
          <StatCard label="Preview Sent" value={counts.preview_sent} color="#8B5CF6" />
          <StatCard label="Paid" value={counts.paid} color="#10B981" />
          <StatCard label="Live" value={counts.live} color="#06B6D4" />
          <StatCard label="Lost" value={counts.lost} color="#6B7280" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
            <input
              data-testid="search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, business..."
              className="w-full bg-[#111827] border border-white/[0.06] text-white pl-10 pr-4 py-2.5 rounded-lg text-sm placeholder:text-slate-600 focus:ring-1 focus:ring-orange-600 focus:border-orange-600 outline-none"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilter("all")} className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${filter === "all" ? "bg-white/10 text-white" : "text-slate-500 hover:text-white"}`} data-testid="filter-all">All</button>
            {STATUSES.map(s => (
              <button key={s} onClick={() => setFilter(s)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${filter === s ? "text-white" : "text-slate-600 hover:text-slate-400"}`} style={filter === s ? { background: `${STATUS_CONFIG[s].color}20`, color: STATUS_CONFIG[s].color } : {}} data-testid={`filter-${s}`}>
                {STATUS_CONFIG[s].label} ({counts[s]})
              </button>
            ))}
          </div>
        </div>

        {/* Lead List */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="animate-spin text-slate-600 mx-auto" size={24} /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-600" data-testid="no-leads">
            <p className="text-lg font-medium mb-1">No leads yet</p>
            <p className="text-sm">When someone fills out the form at /get-started, they'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="lead-list">
            {filtered.map(lead => {
              const isExpanded = expandedLead === lead.lead_id;
              return (
                <div key={lead.lead_id} className="bg-[#111827] border border-white/[0.06] rounded-lg overflow-hidden hover:border-white/10 transition-colors" data-testid={`lead-${lead.lead_id}`}>
                  {/* Lead row */}
                  <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => setExpandedLead(isExpanded ? null : lead.lead_id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-semibold text-sm truncate">{lead.business_name}</h3>
                        <span className="text-slate-600 text-xs shrink-0">{lead.business_type}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><User size={11} />{lead.owner_name}</span>
                        <span className="flex items-center gap-1"><Mail size={11} />{lead.email}</span>
                        {lead.phone && <span className="flex items-center gap-1 hidden sm:flex"><Phone size={11} />{lead.phone}</span>}
                      </div>
                    </div>
                    <StatusBadge status={lead.status} />
                    <span className="text-xs text-slate-700 hidden md:block whitespace-nowrap">{formatDate(lead.created_at)}</span>
                    <ChevronDown size={16} className={`text-slate-600 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-white/[0.04] px-5 py-4 bg-[#0d1225]" data-testid={`lead-detail-${lead.lead_id}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Contact Info</h4>
                          <div className="space-y-2">
                            <p className="text-sm text-slate-300 flex items-center gap-2"><Building2 size={14} className="text-slate-600" />{lead.business_name} ({lead.business_type})</p>
                            <p className="text-sm text-slate-300 flex items-center gap-2"><User size={14} className="text-slate-600" />{lead.owner_name}</p>
                            <a href={`mailto:${lead.email}`} className="text-sm text-orange-400 hover:text-orange-300 flex items-center gap-2"><Mail size={14} className="text-slate-600" />{lead.email}</a>
                            {lead.phone && <a href={`tel:${lead.phone}`} className="text-sm text-orange-400 hover:text-orange-300 flex items-center gap-2"><Phone size={14} className="text-slate-600" />{lead.phone}</a>}
                            <p className="text-sm text-slate-500 flex items-center gap-2"><Clock size={14} className="text-slate-600" />{formatDate(lead.created_at)}</p>
                          </div>
                          {lead.notes && (
                            <div className="mt-4">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Customer Notes</h4>
                              <p className="text-sm text-slate-400 bg-[#0A0F1F] p-3 rounded-md border border-white/[0.04]">{lead.notes}</p>
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Update Status</h4>
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {STATUSES.map(s => (
                              <button key={s} onClick={() => updateStatus(lead.lead_id, s)}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${lead.status === s ? "ring-1" : "opacity-60 hover:opacity-100"}`}
                                style={{ color: STATUS_CONFIG[s].color, background: STATUS_CONFIG[s].bg, ringColor: STATUS_CONFIG[s].color }}
                                data-testid={`status-btn-${s}`}
                              >
                                {STATUS_CONFIG[s].label}
                              </button>
                            ))}
                          </div>
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Admin Notes</h4>
                          {editingNotes === lead.lead_id ? (
                            <div>
                              <textarea value={notesText} onChange={e => setNotesText(e.target.value)} rows={3}
                                className="w-full bg-[#0A0F1F] border border-white/[0.06] text-white p-3 rounded-md text-sm resize-none focus:ring-1 focus:ring-orange-600 outline-none"
                                placeholder="Add notes about this lead..." data-testid="admin-notes-input"
                              />
                              <div className="flex gap-2 mt-2">
                                <button onClick={() => saveNotes(lead.lead_id)} className="px-3 py-1.5 bg-orange-600 text-white text-xs rounded-md font-medium" data-testid="save-notes-btn">Save</button>
                                <button onClick={() => setEditingNotes(null)} className="px-3 py-1.5 text-slate-500 text-xs">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {lead.admin_notes ? <p className="text-sm text-slate-400 bg-[#0A0F1F] p-3 rounded-md border border-white/[0.04] mb-2">{lead.admin_notes}</p> : null}
                              <button onClick={() => { setEditingNotes(lead.lead_id); setNotesText(lead.admin_notes || ""); }}
                                className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1" data-testid="edit-notes-btn"
                              >
                                <FileText size={12} /> {lead.admin_notes ? "Edit notes" : "Add notes"}
                              </button>
                            </div>
                          )}
                          <div className="mt-4 pt-3 border-t border-white/[0.04]">
                            <button onClick={() => deleteLead(lead.lead_id)} className="text-xs text-red-500/60 hover:text-red-400 flex items-center gap-1" data-testid="delete-lead-btn">
                              <Trash2 size={12} /> Delete lead
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
